import { LogLevel } from '@waitroom/logger';
import { STORAGE_GENERAL, UserBasic } from '@waitroom/models';
import { createSelectors } from '@waitroom/state';
import { produce } from 'immer';
import type { LogLevel as StreamLogLevel } from 'livekit-client';
import { create } from 'zustand';
import { envs } from '../services/envs';
import { storageService } from '../services/storage';

export type GeneralSettings = {
  deleteRequests: Dict<boolean> | undefined;
  hostsOnboarded: Dict<boolean> | undefined;
  hideBookDemo: boolean | undefined;
  hideWelcomeToDashboard: boolean | undefined;
  showChecklist: boolean | undefined; // TODO: this flag doesn't need to be persisted
  onboarding: UserBasic['onboarding'] | undefined;
  hideOnboardingPopovers: boolean | undefined;
  hideGenerateAIFeed: boolean | undefined;
  continueInMobWeb: string | undefined;
  debug:
    | {
        logEnabled?: boolean;
        logLevel?: LogLevel;
        streamLogLevel?: StreamLogLevel;
        previewEndpoint?: string;
      }
    | undefined;
};

const storageState = storageService.getParsed<GeneralSettings>(STORAGE_GENERAL);
export const initialState: GeneralSettings = {
  deleteRequests: undefined,
  hostsOnboarded: undefined,
  hideBookDemo: undefined,
  hideWelcomeToDashboard: undefined,
  showChecklist: false,
  hideOnboardingPopovers: undefined,
  hideGenerateAIFeed: undefined,
  continueInMobWeb: undefined,
  onboarding: undefined,
  ...storageState,
  debug: {
    ...storageState?.debug,
    logLevel: Number(storageState?.debug?.logLevel ?? envs.VITE_LOG_LEVEL ?? 0) as LogLevel,
    streamLogLevel: Number(storageState?.debug?.streamLogLevel ?? 4),
  },
};

const useGeneralSettingsStoreBase = create<GeneralSettings>()(() => initialState);

export const useGeneralSettingsStore = createSelectors(useGeneralSettingsStoreBase);

export const updateGeneralSettings = (
  update: GeneralSettings | ((prev: GeneralSettings) => GeneralSettings),
) => {
  useGeneralSettingsStoreBase.setState((prev) => {
    const updated = typeof update === 'function' ? update(prev) : update;
    storageService.set(STORAGE_GENERAL, updated);
    return updated;
  });
};

export const mergeGeneralSettings = (settings: Partial<GeneralSettings>) => {
  useGeneralSettingsStoreBase.setState((prev) => {
    const updated = { ...prev, ...settings };
    storageService.set(STORAGE_GENERAL, updated);
    return updated;
  });
};

export const updateDebugSettings = (updated: Partial<GeneralSettings['debug']>) => {
  updateGeneralSettings((prev) =>
    produce(prev, (draft) => {
      draft.debug = {
        ...prev.debug,
        ...updated,
      };
    }),
  );
};
